import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { toast } from "solid-sonner";
import { axiosInstance } from "@/api/client";
import {
  ATS_KEY,
  COMPANY_KEY,
  JOB_OFFER_KEY,
  PROFILE_KEY,
} from "@/api/query-keys";
import { getApiErrorMessage } from "@/api/utils/error";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import type { JobOfferPopulatedWithCompanyAndPostedBy } from "@/domains/job-offer/types";
import type {
  ApplicationStatus,
  JobApplication,
  JobApplicationInsert,
} from "../types";

export type ApplyForJobOfferPayload = JobApplicationInsert & {
  additionalDocuments?: File[];
};

const applyForJobOffer = async (payload: ApplyForJobOfferPayload) => {
  const { additionalDocuments, ...restPayload } = payload;

  // If there are additional documents, use FormData
  if (additionalDocuments && additionalDocuments.length > 0) {
    const formData = new FormData();

    // Add all regular fields to FormData as strings/numbers
    Object.entries(restPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === "object" && !(value instanceof File)) {
          // For objects, stringify them
          formData.append(key, JSON.stringify(value));
        } else {
          // For primitives, convert to string
          formData.append(key, String(value));
        }
      }
    });

    // Add additional documents with the correct field name
    additionalDocuments.forEach((file) => {
      formData.append("additionalDocuments", file);
    });

    const { data } = await axiosInstance.post<{
      job_application: JobApplication;
    }>(`/job-offer/application`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  }

  // Otherwise, send as JSON
  const { data } = await axiosInstance.post<{
    job_application: JobApplication;
  }>(`/job-offer/application`, restPayload as JobApplicationInsert);
  return data;
};

export const useApplyForJobOffer = () => {
  const queryClient = useQueryClient();

  return useMutation(() => ({
    mutationFn: (payload: ApplyForJobOfferPayload) => applyForJobOffer(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [JOB_OFFER_KEY, "user-applications"],
      });
    },
  }));
};

const applicationPezzotta = async (
  payload: Pick<JobApplicationInsert, "job_offer_id">,
) => {
  const { data } = await axiosInstance.patch(
    `/job-offer/applications-count/${payload.job_offer_id}`,
  );
  return data;
};

export const useApplicationPezzotta = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (payload: Pick<JobApplicationInsert, "job_offer_id">) =>
      applicationPezzotta(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [JOB_OFFER_KEY, "user-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: [PROFILE_KEY],
      });
    },
  }));
};

const getUserApplications = async () => {
  const { data } = await axiosInstance.get<{
    job_application: JobApplication[];
    job_offers: JobOfferPopulatedWithCompanyAndPostedBy[];
  }>(`/job-offer/application/profile`);
  return data;
};

export const useGetUserApplications = () => {
  const [auth] = useAuthV2();
  return useQuery(() => ({
    queryKey: [JOB_OFFER_KEY, "user-applications"],
    queryFn: () => getUserApplications(),
    enabled: !!auth.profile?.id,
  }));
};

const markJobApplicationAsViewed = async (jobApplicationId: string) => {
  const { data } = await axiosInstance.post<{
    job_application: JobApplication;
  }>(`/job-offer/application/${jobApplicationId}/view`);
  return data;
};

export const useMarkJobApplicationAsViewed = () => {
  return useMutation(() => ({
    mutationFn: (jobApplicationId: string) =>
      markJobApplicationAsViewed(jobApplicationId),
  }));
};

export type UpdateFreelancerApplicationStatusPayload = {
  jobApplicationId: string;
  status: ApplicationStatus;
};

const updateFreelancerApplicationStatus = async ({
  jobApplicationId,
  status,
}: UpdateFreelancerApplicationStatusPayload) => {
  const { data } = await axiosInstance.patch<{
    job_application: JobApplication;
  }>(`/job-offer/application/${jobApplicationId}/status`, { status });
  return data;
};

export const useUpdateFreelancerApplicationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(() => ({
    mutationFn: (payload: UpdateFreelancerApplicationStatusPayload) =>
      updateFreelancerApplicationStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({
        queryKey: [JOB_OFFER_KEY, "user-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: [ATS_KEY, "job-applications"],
      });
    },
    onError: (error) => {
      toast.error(
        getApiErrorMessage(
          error,
          "Errore durante l'aggiornamento dello stato.",
        ),
      );
    },
  }));
};
